import type { MutableRefObject, ReactNode, RefObject } from "react"
import { useEffect, useRef } from "react"

import {
	InternalPopover,
	InternalPopoverAnchor,
	InternalPopoverContent,
	InternalPopoverPortal,
} from "@productlane/ui/src/components/internal/popover"

interface Props<T> {
	open: boolean
	onOpenChange: (open: boolean) => void
	/** Resolved from the input, so the list portals inside the sheet dialog. */
	container?: HTMLElement | null
	anchorRef: RefObject<HTMLElement | null>
	anchor: ReactNode
	listId: string
	items: readonly T[]
	activeIndex: number
	keyOf: (item: T) => string
	groupOf?: (item: T) => string
	renderRow: (item: T, active: boolean) => ReactNode
	onPick: (item: T, index: number) => void
	onHover: (index: number) => void
	keyboardRef: MutableRefObject<boolean>
	empty?: ReactNode
}

export function AnchoredSuggestions<T>({
	open,
	onOpenChange,
	container,
	anchorRef,
	anchor,
	listId,
	items,
	activeIndex,
	keyOf,
	groupOf,
	renderRow,
	onPick,
	onHover,
	keyboardRef,
	empty,
}: Props<T>) {
	const listRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		if (!keyboardRef.current || activeIndex < 0) return
		listRef.current
			?.querySelector(`[data-index="${activeIndex}"]`)
			?.scrollIntoView({ block: "nearest" })
	}, [activeIndex, keyboardRef])

	let lastGroup: string | null = null

	return (
		<InternalPopover open={open} onOpenChange={onOpenChange} modal={false}>
			<InternalPopoverAnchor asChild>{anchor}</InternalPopoverAnchor>
			<InternalPopoverPortal container={container ?? undefined}>
				<InternalPopoverContent
					ref={listRef}
					id={listId}
					role="listbox"
					align="start"
					side="bottom"
					collisionPadding={12}
					className="w-[var(--radix-popover-trigger-width)] min-w-56 border-0 p-0 pb-2 ring-1 ring-black/5 dark:ring-white/10"
					onOpenAutoFocus={(event) => event.preventDefault()}
					onCloseAutoFocus={(event) => event.preventDefault()}
					onInteractOutside={(event) => {
						if (anchorRef.current?.contains(event.target as Node)) {
							event.preventDefault()
						}
					}}
				>
					{items.length === 0 ? empty : null}
					{items.map((item, index) => {
						const group = groupOf?.(item) ?? null
						const heading = group && group !== lastGroup ? group : null
						lastGroup = group
						const active = index === activeIndex
						return (
							<div key={keyOf(item)}>
								{heading ? (
									<p className="px-3 pb-1 pt-2 text-[11px] font-medium tracking-wide text-tertiary">
										{heading}
									</p>
								) : null}
								<button
									type="button"
									tabIndex={-1}
									role="option"
									id={`${listId}-${index}`}
									data-index={index}
									aria-selected={active}
									className={`flex w-full cursor-default items-center gap-2 px-3 py-1.5 text-left ${
										active ? "bg-gray-100 dark:bg-gray-800" : ""
									}`}
									onMouseDown={(event) => {
										// Beats the input's blur, so the pick lands on a focused input.
										event.preventDefault()
										onPick(item, index)
									}}
									onMouseEnter={() => {
										keyboardRef.current = false
										onHover(index)
									}}
								>
									{renderRow(item, active)}
								</button>
							</div>
						)
					})}
				</InternalPopoverContent>
			</InternalPopoverPortal>
		</InternalPopover>
	)
}
